import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Brain, Play, RotateCcw, Check, X, Lock, Trophy, Zap, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MemoryPage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeExercise, setActiveExercise] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);

  const c = {
    back: 'Tillbaka',
    title: 'Minnesträning',
    upgrade: 'Uppgradera för tillgång',
    upgradeDesc: 'Minnesträning ingår i premium.',
    upgradeBtn: 'Uppgradera nu'
  };

  useEffect(() => {
    if (hasAccess) {
      fetchExercises();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API}/api/memory/exercises`, { headers: getAuthHeader() });
      setExercises(response.data);
    } catch (error) {
      console.error('Error fetching memory exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const startExercise = (exercise) => {
    setActiveExercise(exercise);
    setScore(0);
    setStreak(0);
    
    if (exercise.type === 'sequence') {
      // Number sequence memory
      const sequence = Array.from({ length: 4 + Math.floor(score / 20) }, () => 
        Math.floor(Math.random() * 10)
      );
      setGameState({
        phase: 'show',
        sequence,
        userInput: [],
        showIndex: 0
      });
      
      // Show sequence one by one
      let index = 0;
      const interval = setInterval(() => {
        setGameState(prev => ({ ...prev, showIndex: index + 1 }));
        index++;
        if (index >= sequence.length) {
          clearInterval(interval);
          setTimeout(() => {
            setGameState(prev => ({ ...prev, phase: 'input' }));
          }, 500);
        }
      }, 800);
    } else if (exercise.type === 'word_pairs') {
      // Word pair memory
      const pairs = exercise.content.pairs.slice(0, 3 + Math.floor(score / 30));
      setGameState({
        phase: 'study',
        pairs,
        currentPair: 0,
        correctAnswers: 0
      });
    }
  };

  const handleSequenceInput = (num) => {
    const newInput = [...gameState.userInput, num];
    setGameState(prev => ({ ...prev, userInput: newInput }));
    
    // Check if complete
    if (newInput.length === gameState.sequence.length) {
      const isCorrect = newInput.every((n, i) => n === gameState.sequence[i]);
      if (isCorrect) {
        setScore(prev => prev + 10);
        setStreak(prev => prev + 1);
        setGameState(prev => ({ ...prev, phase: 'correct' }));
      } else {
        setStreak(0);
        setGameState(prev => ({ ...prev, phase: 'wrong' }));
      }
      
      setTimeout(() => {
        if (isCorrect) {
          startExercise(activeExercise); // Next round
        } else {
          setGameState(null);
          setActiveExercise(null);
        }
      }, 1500);
    }
  };

  const handleWordPairAnswer = (answer) => {
    const currentPair = gameState.pairs[gameState.currentPair];
    const isCorrect = answer.toLowerCase() === currentPair.answer.toLowerCase();
    
    if (isCorrect) {
      setScore(prev => prev + 15);
      setStreak(prev => prev + 1);
    } else {
      setStreak(0);
    }
    
    if (gameState.currentPair + 1 < gameState.pairs.length) {
      setGameState(prev => ({
        ...prev,
        currentPair: prev.currentPair + 1,
        correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0)
      }));
    } else {
      setGameState(prev => ({
        ...prev,
        phase: 'complete',
        correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0)
      }));
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-white" data-testid="memory-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard', { state: { expandCategory: 'mind' } })} className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-2xl font-semibold">{c.title}</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center space-y-6">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground/50" />
            <h2 className="font-heading text-2xl font-semibold">{c.upgrade}</h2>
            <p className="text-muted-foreground">{c.upgradeDesc}</p>
            <Button onClick={() => navigate('/subscription')} className="rounded-full btn-warm" data-testid="upgrade-btn">
              {c.upgradeBtn}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="memory-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard', { state: { expandCategory: 'mind' } })} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-2xl font-semibold">{c.title}</h1>
          {score > 0 && (
            <div className="ml-auto flex items-center gap-3">
              <span className="flex items-center gap-1 text-sm">
                <Trophy className="w-4 h-4 text-primary" />
                {score}
              </span>
              {streak > 1 && (
                <span className="flex items-center gap-1 text-sm text-orange-500">
                  <Zap className="w-4 h-4" />
                  {streak}x
                </span>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {!activeExercise ? (
          <>
            {/* Intro */}
            <div className="text-center space-y-3 animate-fade-in">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-violet-100 flex items-center justify-center">
                <Brain className="w-8 h-8 text-violet-600" />
              </div>
              <h2 className="font-heading text-3xl font-semibold">Träna hjärnan</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Håll hjärnan skarp med roliga minnesövningar. Regelbunden träning stärker koncentration och minne.
              </p>
            </div>

            {/* Exercise Grid */}
            <div className="grid gap-4 md:grid-cols-2">
              {loading ? (
                [1, 2, 3, 4].map(i => (
                  <div key={i} className="h-40 bg-secondary/50 rounded-2xl animate-pulse" />
                ))
              ) : (
                exercises.map((exercise, index) => (
                  <button
                    key={exercise.id}
                    onClick={() => startExercise(exercise)}
                    className="bg-card rounded-2xl border border-border/50 p-6 text-left hover:shadow-lg transition-shadow animate-slide-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`exercise-${exercise.id}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Brain className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-heading text-lg font-medium">{exercise.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2">{exercise.description}</p>
                    <div className="mt-4 flex items-center gap-2 text-xs text-primary">
                      <Play className="w-3 h-3" />
                      Starta övning
                    </div>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          /* Active Game */
          <div className="max-w-md mx-auto space-y-6">
            <div className="text-center">
              <h3 className="font-heading text-xl font-medium">{activeExercise.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{activeExercise.instructions}</p>
            </div>

            {/* Sequence Game */}
            {activeExercise.type === 'sequence' && gameState && (
              <div className="space-y-6">
                {gameState.phase === 'show' && (
                  <div className="text-center space-y-4">
                    <p className="text-sm text-muted-foreground">Memorera sekvensen:</p>
                    <div className="flex justify-center gap-2">
                      {gameState.sequence.map((num, i) => (
                        <div
                          key={i}
                          className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all ${
                            i < gameState.showIndex
                              ? 'bg-primary text-primary-foreground scale-100'
                              : 'bg-secondary scale-90 opacity-50'
                          }`}
                        >
                          {i < gameState.showIndex ? num : '?'}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {gameState.phase === 'input' && (
                  <div className="space-y-4">
                    <p className="text-center text-sm text-muted-foreground">
                      Ange sekvensen ({gameState.userInput.length}/{gameState.sequence.length}):
                    </p>
                    <div className="flex justify-center gap-2 mb-4">
                      {gameState.sequence.map((_, i) => (
                        <div
                          key={i}
                          className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold ${
                            i < gameState.userInput.length
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary'
                          }`}
                        >
                          {gameState.userInput[i] ?? ''}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((num) => (
                        <Button
                          key={num}
                          onClick={() => handleSequenceInput(num)}
                          className="h-14 text-xl rounded-xl"
                          variant="outline"
                          data-testid={`num-${num}`}
                        >
                          {num}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {gameState.phase === 'correct' && (
                  <div className="text-center space-y-4 animate-fade-in">
                    <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                      <Check className="w-10 h-10 text-green-500" />
                    </div>
                    <p className="font-heading text-xl">Rätt!</p>
                  </div>
                )}

                {gameState.phase === 'wrong' && (
                  <div className="text-center space-y-4 animate-fade-in">
                    <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                      <X className="w-10 h-10 text-red-500" />
                    </div>
                    <p className="font-heading text-xl">Fel</p>
                    <p className="text-sm text-muted-foreground">
                      Rätt svar: {gameState.sequence.join(' ')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Word Pairs Game */}
            {activeExercise.type === 'word_pairs' && gameState && (
              <div className="space-y-6">
                {gameState.phase === 'study' && (
                  <div className="text-center space-y-4">
                    <p className="text-sm text-muted-foreground">Memorera ordparen:</p>
                    <div className="space-y-3">
                      {gameState.pairs.map((pair, i) => (
                        <div
                          key={i}
                          className="p-4 bg-card rounded-xl border border-border/50"
                        >
                          <span className="font-medium">{pair.word}</span>
                          <span className="mx-2 text-muted-foreground">→</span>
                          <span className="font-medium text-primary">{pair.answer}</span>
                        </div>
                      ))}
                    </div>
                    <Button
                      onClick={() => setGameState(prev => ({ ...prev, phase: 'test' }))}
                      className="rounded-full"
                    >
                      Jag är redo!
                    </Button>
                  </div>
                )}

                {gameState.phase === 'test' && (
                  <div className="text-center space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Vad hör ihop med detta ord?
                    </p>
                    <p className="text-2xl font-heading font-medium">
                      {gameState.pairs[gameState.currentPair].word}
                    </p>
                    <input
                      type="text"
                      placeholder="Ditt svar..."
                      className="w-full p-4 rounded-xl border border-border/50 bg-background text-center focus:outline-none focus:ring-2 focus:ring-primary/50"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleWordPairAnswer(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      autoFocus
                      data-testid="word-answer-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Tryck Enter för att svara
                    </p>
                  </div>
                )}

                {gameState.phase === 'complete' && (
                  <div className="text-center space-y-4 animate-fade-in">
                    <Trophy className="w-16 h-16 mx-auto text-primary" />
                    <p className="font-heading text-xl">Klart!</p>
                    <p className="text-muted-foreground">
                      Du fick {gameState.correctAnswers} av {gameState.pairs.length} rätt
                    </p>
                    <Button
                      onClick={() => {
                        setGameState(null);
                        setActiveExercise(null);
                      }}
                      variant="outline"
                      className="rounded-full"
                    >
                      Tillbaka
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Exit Button */}
            {gameState?.phase !== 'complete' && (
              <Button
                onClick={() => {
                  setGameState(null);
                  setActiveExercise(null);
                }}
                variant="ghost"
                className="w-full rounded-xl"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Avsluta
              </Button>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-8">
          <p className="text-xs text-muted-foreground">
            Regelbunden hjärnträning håller sinnet skarpt och förbättrar koncentrationen.
          </p>
        </div>
      </main>
    </div>
  );
}
